import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      track("Signup");
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12 sm:py-16 fade-up">
      <div className="card-warm p-8">
        <h1 className="font-serif-display text-3xl font-semibold" style={{ color: "#1C2826" }}>
          Create your account
        </h1>
        <p className="mt-2 text-sm" style={{ color: "#5C6B66" }}>
          Free to start. No credit card required.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              data-testid="register-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1.5 h-12 rounded-xl"
              placeholder="Your name"
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="register-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5 h-12 rounded-xl"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              data-testid="register-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="mt-1.5 h-12 rounded-xl"
              placeholder="Min 8 chars, with a letter and a number"
            />
          </div>

          {error && (
            <div
              data-testid="register-error"
              className="text-sm rounded-xl p-3"
              style={{ background: "#FEF2F2", color: "#991B1B", border: "1px solid #FCA5A5" }}
            >
              {error}
            </div>
          )}

          <Button
            data-testid="register-submit-btn"
            type="submit"
            disabled={loading}
            className="btn-brand w-full h-12 rounded-full text-sm font-semibold"
          >
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            Create account
          </Button>
        </form>

        <p className="mt-6 text-sm text-center" style={{ color: "#5C6B66" }}>
          Already have an account?{" "}
          <Link to="/login" data-testid="go-to-login-link" className="font-semibold" style={{ color: "#2D5A4C" }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
